/*
 * src/hooks/useSessionManager.ts
 * 
 * Comprehensive session management hook that handles:
 * - JWT token expiration checking
 * - Idle timeout with user activity tracking
 * - Warning notifications before auto-logout
 * - Graceful session cleanup
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { sessionTimeoutLogout } from '../utils/auth';
import { getSessionConfig } from '../config/session';
import { setSessionData, updateLastActivity, clearSessionData, setUserAuthenticated, setToken } from '../store/slices/userSlice';

// Get session configuration based on environment
const SESSION_CONFIG = getSessionConfig();

interface SessionWarningData {
  show: boolean;
  timeRemaining: number;
  type: 'idle' | 'absolute' | 'token';
}

export interface UseSessionManagerReturn {
  isWarningVisible: boolean;
  warningData: SessionWarningData;
  extendSession: () => void;
  getRemainingTime: () => { idle: number; absolute: number };
  isTokenValid: boolean;
}

// Utility function to decode JWT and check expiration
const decodeJWT = (token: string): { exp?: number; iat?: number } | null => {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', (error as any)?.message || String(error));
    return null;
  }
};

// Check if JWT token is expired or will expire soon
const isTokenExpired = (token: string, bufferTime: number = SESSION_CONFIG.TOKEN_EXPIRY_BUFFER): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded?.exp) return true;
  
  const now = Date.now();
  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  
  return (expiryTime - now) <= bufferTime;
};

// Get token expiration time
const getTokenExpiryTime = (token: string): number => {
  const decoded = decodeJWT(token);
  if (!decoded?.exp) return 0;
  return decoded.exp * 1000;
};

export const useSessionManager = (): UseSessionManagerReturn => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
  const sessionData = useSelector((state: RootState) => state.user.sessionData);
  
  // Session tracking refs - keep for in-memory optimization, but sync with Redux
  const lastActivityTime = useRef<number>(sessionData?.lastActivity || Date.now());
  const sessionStartTime = useRef<number>(sessionData?.sessionStart || Date.now());
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const absoluteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const absoluteWarningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isWarningShowing = useRef<boolean>(false);
  const isLoggingOut = useRef<boolean>(false);
  const hasInitialized = useRef<boolean>(false);
  
  // Warning state
  const [warningData, setWarningData] = useState<SessionWarningData>({
    show: false,
    timeRemaining: 0,
    type: 'idle',
  });
  
  const [isTokenValid, setIsTokenValid] = useState<boolean>(true);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
    if (absoluteTimeoutRef.current) {
      clearTimeout(absoluteTimeoutRef.current);
      absoluteTimeoutRef.current = null;
    }
    if (absoluteWarningTimeoutRef.current) {
      clearTimeout(absoluteWarningTimeoutRef.current);
      absoluteWarningTimeoutRef.current = null;
    }
  }, []);

  // Perform logout with reason logging
  const performLogout = useCallback(async (reason: string) => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;

    clearAllTimers();
    isWarningShowing.current = false;
    setWarningData({ show: false, timeRemaining: 0, type: 'idle' });
    
    // CRITICAL: Mark as unauthenticated IMMEDIATELY to prevent the useEffect
    // from re-initializing a new session between clearSessionData and
    // comprehensiveReset dispatching setUserAuthenticated(false).
    dispatch(setUserAuthenticated(false));
    dispatch(setToken(null));
    dispatch(clearSessionData());
    
    try {
      await sessionTimeoutLogout(reason);
    } catch (error) {
      console.error('Error during session timeout logout:', error);
      if (typeof window !== 'undefined') {
        window.location.href = '/log-ind/';
      }
    } finally {
      isLoggingOut.current = false;
    }
  }, [clearAllTimers, dispatch]);

  // Show session warning
  const showWarning = useCallback((type: 'idle' | 'absolute' | 'token', timeRemaining: number) => {
    if (isWarningShowing.current || isLoggingOut.current) {
      return;
    }

    isWarningShowing.current = true;
    setWarningData({
      show: true,
      timeRemaining,
      type,
    });

  }, []);

  // Reset idle timer
  const resetIdleTimer = useCallback(() => {
    if (!isAuthenticated || !token || isLoggingOut.current) return;

    const now = Date.now();
    lastActivityTime.current = now;
    
    // Update Redux state for persistence (do this async to avoid triggering useEffect)
    setTimeout(() => {
      dispatch(updateLastActivity(now));
    }, 0);
    
    // Clear existing timers
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // Hide warning if it was showing for idle timeout (check current state directly)
    setWarningData(prev => {
      if (prev.show && prev.type === 'idle') {
        isWarningShowing.current = false;
        return { show: false, timeRemaining: 0, type: 'idle' };
      }
      return prev;
    });
    
    // Set warning timeout
    const warningDelay = SESSION_CONFIG.IDLE_TIMEOUT - SESSION_CONFIG.WARNING_TIME;
    warningTimeoutRef.current = setTimeout(() => {
      const { idle } = getRemainingTime();
      const actualTimeRemaining = Math.min(idle, SESSION_CONFIG.WARNING_TIME);
      showWarning('idle', actualTimeRemaining);
    }, warningDelay);
    
    // Set logout timeout
    idleTimeoutRef.current = setTimeout(() => {
      performLogout('inactivity timeout');
    }, SESSION_CONFIG.IDLE_TIMEOUT);
    
  }, [isAuthenticated, token, showWarning, performLogout, dispatch]);

  // Check token validity
  const checkTokenValidity = useCallback(() => {
    if (!token || !isAuthenticated) return;

    const isExpired = isTokenExpired(token, SESSION_CONFIG.WARNING_TIME);
    const willExpireSoon = isTokenExpired(token);
    
    setIsTokenValid(!isExpired);

    if (isExpired) {
      performLogout('token expired');
    } else if (willExpireSoon) {
      const expiryTime = getTokenExpiryTime(token);
      const timeRemaining = Math.max(0, expiryTime - Date.now());
      showWarning('token', timeRemaining);
    }
  }, [token, isAuthenticated, performLogout, showWarning]);

  // Handle user activity - but ignore activity when warning is showing or logging out
  const handleActivity = useCallback((event: Event) => {
    if (isWarningShowing.current || isLoggingOut.current) {
      return;
    }
    
    resetIdleTimer();
  }, [resetIdleTimer]);

  // 🔍 DEBUG: Expose timer status for debugging
  if (typeof window !== 'undefined') {
    (window as any).checkSessionTimers = () => {
      console.log('🔍 [Session Debug] Timer Status:', {
        idleTimerActive: !!idleTimeoutRef.current,
        warningTimerActive: !!warningTimeoutRef.current,
        tokenCheckActive: !!tokenCheckIntervalRef.current,
        absoluteTimerActive: !!absoluteTimeoutRef.current,
        isWarningShowing: isWarningShowing.current,
        hasInitialized: hasInitialized.current,
        warningData: warningData
      });
    };
  }

  // Extend session (when user clicks "Stay logged in")
  const extendSession = useCallback(() => {
    if (isLoggingOut.current) return;

    const now = Date.now();
    
    // Reset session start time for absolute timeout
    sessionStartTime.current = now;
    
    // Update Redux state
    dispatch(setSessionData({ sessionStart: now, lastActivity: now }));
    
    // Reset idle timer
    resetIdleTimer();
    
    // Hide warning
    isWarningShowing.current = false;
    setWarningData({ show: false, timeRemaining: 0, type: 'idle' });
    
    // Reset absolute timeout only if max session duration is enabled
    if (!SESSION_CONFIG.DISABLE_MAX_SESSION_DURATION) {
      if (absoluteTimeoutRef.current) {
        clearTimeout(absoluteTimeoutRef.current);
      }
      if (absoluteWarningTimeoutRef.current) {
        clearTimeout(absoluteWarningTimeoutRef.current);
      }
      
      // Set new absolute timeout with warning
      const absoluteWarningTime = SESSION_CONFIG.MAX_SESSION_DURATION - SESSION_CONFIG.WARNING_TIME;
      absoluteWarningTimeoutRef.current = setTimeout(() => {
        const { absolute } = getRemainingTime();
        const actualTimeRemaining = Math.min(absolute, SESSION_CONFIG.WARNING_TIME);
        showWarning('absolute', actualTimeRemaining);
      }, absoluteWarningTime);
      
      absoluteTimeoutRef.current = setTimeout(() => {
        performLogout('maximum session duration reached');
      }, SESSION_CONFIG.MAX_SESSION_DURATION);
    }
    
  }, [resetIdleTimer, showWarning, performLogout, dispatch]);

  // Get remaining time
  const getRemainingTime = useCallback(() => {
    const now = Date.now();
    const idleRemaining = Math.max(0, 
      SESSION_CONFIG.IDLE_TIMEOUT - (now - lastActivityTime.current)
    );
    const absoluteRemaining = Math.max(0, 
      SESSION_CONFIG.MAX_SESSION_DURATION - (now - sessionStartTime.current)
    );
    
    return { idle: idleRemaining, absolute: absoluteRemaining };
  }, []);

  // Handle tab visibility change — check session validity when tab becomes visible
  // (setTimeout callbacks are suspended during sleep, so we need an immediate check)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isAuthenticated || !token || isLoggingOut.current) return;

      const now = Date.now();
      const sessionStart = sessionData?.sessionStart || sessionStartTime.current;
      const lastActivity = sessionData?.lastActivity || lastActivityTime.current;

      if (!sessionStart || !lastActivity) return;

      const timeSinceLastActivity = now - lastActivity;
      const sessionDuration = now - sessionStart;

      if (timeSinceLastActivity > SESSION_CONFIG.IDLE_TIMEOUT) {
        performLogout('idle timeout exceeded on tab resume');
        return;
      }
      if (!SESSION_CONFIG.DISABLE_MAX_SESSION_DURATION && sessionDuration > SESSION_CONFIG.MAX_SESSION_DURATION) {
        performLogout('maximum session duration exceeded on tab resume');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, token, sessionData, performLogout]);

  // Initialize session management
  useEffect(() => {
    if (!isAuthenticated || !token) {
      clearAllTimers();
      isWarningShowing.current = false;
      hasInitialized.current = false;
      setWarningData({ show: false, timeRemaining: 0, type: 'idle' });
      return;
    }

    // Don't re-initialize while an async logout is in progress
    if (isLoggingOut.current) return;

    const timersActive = idleTimeoutRef.current !== null || warningTimeoutRef.current !== null;
    
    if (hasInitialized.current && timersActive) {
      return;
    }
    
    if (hasInitialized.current && !timersActive) {
      hasInitialized.current = false;
    }
    
    clearAllTimers();
    
    isWarningShowing.current = false;
    setWarningData({ show: false, timeRemaining: 0, type: 'idle' });
    
    // STARTUP VALIDATION: Check if persisted session is still valid
    const now = Date.now();
    
    if (isTokenExpired(token)) {
      performLogout('token expired on startup');
      return;
    }
    
    let isInWarningPeriod = false;
    
    if (sessionData?.sessionStart && sessionData?.lastActivity) {
      const timeSinceLastActivity = now - sessionData.lastActivity;
      const sessionDuration = now - sessionData.sessionStart;
      
      sessionStartTime.current = sessionData.sessionStart;
      lastActivityTime.current = sessionData.lastActivity;
      
      if (timeSinceLastActivity > SESSION_CONFIG.IDLE_TIMEOUT) {
        performLogout('idle timeout exceeded on startup');
        return;
      }
      
      if (!SESSION_CONFIG.DISABLE_MAX_SESSION_DURATION && sessionDuration > SESSION_CONFIG.MAX_SESSION_DURATION) {
        performLogout('maximum session duration exceeded on startup');
        return;
      }
      
      const idleWarningThreshold = SESSION_CONFIG.IDLE_TIMEOUT - SESSION_CONFIG.WARNING_TIME;
      
      if (idleWarningThreshold > 0 && timeSinceLastActivity > idleWarningThreshold) {
        isInWarningPeriod = true;
        const remainingIdleTime = SESSION_CONFIG.IDLE_TIMEOUT - timeSinceLastActivity;
        
        absoluteWarningTimeoutRef.current = setTimeout(() => {
          showWarning('idle', Math.max(0, remainingIdleTime));
        }, 100);
        
        idleTimeoutRef.current = setTimeout(() => {
          performLogout('inactivity timeout');
        }, Math.max(0, remainingIdleTime));
      }
      
      if (!SESSION_CONFIG.DISABLE_MAX_SESSION_DURATION) {
        const absoluteWarningThreshold = SESSION_CONFIG.MAX_SESSION_DURATION - SESSION_CONFIG.WARNING_TIME;
        if (absoluteWarningThreshold > 0 && sessionDuration > absoluteWarningThreshold) {
          isInWarningPeriod = true;
          const remainingAbsoluteTime = SESSION_CONFIG.MAX_SESSION_DURATION - sessionDuration;
          
          absoluteWarningTimeoutRef.current = setTimeout(() => {
            showWarning('absolute', Math.max(0, remainingAbsoluteTime));
          }, 100);
          
          absoluteTimeoutRef.current = setTimeout(() => {
            performLogout('maximum session duration reached');
          }, Math.max(0, remainingAbsoluteTime));
        }
      }

    } else {
      sessionStartTime.current = now;
      lastActivityTime.current = now;
      dispatch(setSessionData({ sessionStart: now, lastActivity: now }));
    }
    
    checkTokenValidity();
    
    tokenCheckIntervalRef.current = setInterval(checkTokenValidity, SESSION_CONFIG.TOKEN_CHECK_INTERVAL);
    
    if (!isInWarningPeriod) {
      if (!SESSION_CONFIG.DISABLE_MAX_SESSION_DURATION) {
        const sessionAge = now - sessionStartTime.current;
        const remainingSessionTime = SESSION_CONFIG.MAX_SESSION_DURATION - sessionAge;
        
        if (remainingSessionTime > SESSION_CONFIG.WARNING_TIME) {
          const absoluteWarningTime = remainingSessionTime - SESSION_CONFIG.WARNING_TIME;
          absoluteWarningTimeoutRef.current = setTimeout(() => {
            const { absolute } = getRemainingTime();
            const actualTimeRemaining = Math.min(absolute, SESSION_CONFIG.WARNING_TIME);
            showWarning('absolute', actualTimeRemaining);
          }, absoluteWarningTime);
        } else if (remainingSessionTime > 0) {
          showWarning('absolute', remainingSessionTime);
        }
        
        if (remainingSessionTime > 0) {
          absoluteTimeoutRef.current = setTimeout(() => {
            performLogout('maximum session duration reached');
          }, remainingSessionTime);
        }
      }
      
      resetIdleTimer();
    }
    
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });
    
    hasInitialized.current = true;
    
    return () => {
      clearAllTimers();
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [isAuthenticated, token, performLogout, checkTokenValidity, resetIdleTimer, handleActivity, showWarning, dispatch, sessionData?.sessionStart]);

  // Update warning countdown
  useEffect(() => {
    if (!warningData.show) return;

    const interval = setInterval(() => {
      setWarningData(prev => {
        const newTimeRemaining = prev.timeRemaining - 1000;
        if (newTimeRemaining <= 0) {
          clearInterval(interval);
          isWarningShowing.current = false;
          return { ...prev, show: false, timeRemaining: 0 };
        }
        return { ...prev, timeRemaining: newTimeRemaining };
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [warningData.show]);

  return {
    isWarningVisible: warningData.show,
    warningData,
    extendSession,
    getRemainingTime,
    isTokenValid,
  };
}; 